/**
 * Every read and write the app makes.
 *
 * Kept in one place so there is a single answer to "where does this figure come
 * from" — which is the whole premise of v2.
 */

import { supabase } from "./supabase";
import { addDays, isoDate } from "../engine/dates";

/** Scoro tables are shared with v1 and written only by its hourly sync. */
export async function loadAll() {
  const [invoices, bills, scoroOverrides, expected, docSpend, recurring, amexSettings, amexCharges, syncRuns, updates, quotes, quoteStatuses, quoteRules, onAccount, quoteOverrides, paidInvoices, instalments, amexPayments, paidMarks, dismissals, retainers, retainerSettings, manualEntries, appSettings, investorComments, paydownOverrides, recurringOverrides, xeroBills, xeroRuns, decisions, bankReadings, xeroPaidBills] =
    await Promise.all([
      supabase.from("scoro_invoices").select("*").order("deadline", { ascending: true }),
      supabase.from("scoro_bills").select("*").order("deadline", { ascending: true }),
      supabase.from("scoro_payment_overrides").select("*"),
      supabase.from("expected_payment_dates").select("*"),
      supabase.from("doc_spend").select("*"),
      supabase.from("recurring_costs").select("*"),
      supabase.from("amex_settings").select("*").maybeSingle(),
      supabase.from("amex_charges").select("*"),
      supabase.from("scoro_sync_runs").select("*").order("started_at", { ascending: false }).limit(1),
      supabase.from("doc_updates").select("*").order("created_at", { ascending: false }),
      supabase.from("scoro_quotes").select("*"),
      supabase.from("scoro_quote_statuses").select("*"),
      supabase.from("quote_forecast_rules").select("*"),
      supabase.from("on_account_payments").select("*").order("paid_date", { ascending: false }),
      supabase.from("quote_forecast_overrides").select("*"),
      supabase.from("scoro_paid_invoices").select("*").order("paid_date", { ascending: false }),
      supabase.from("doc_instalments").select("*").order("due_date"),
      supabase.from("amex_payments").select("*").order("payment_date"),
      supabase.from("doc_paid_marks").select("*"),
      supabase.from("doc_dismissals").select("*"),
      supabase.from("scoro_retainers").select("*").order("term_end"),
      supabase.from("retainer_settings").select("*"),
      supabase.from("manual_entries").select("*").order("entry_date", { ascending: false }),
      supabase.from("app_settings").select("*").maybeSingle(),
      supabase.from("investor_comments").select("*").order("created_at", { ascending: false }),
      supabase.from("amex_paydown_overrides").select("*"),
      supabase.from("recurring_overrides").select("*"),
      supabase.from("xero_bills").select("*").order("due_date"),
      supabase.from("xero_sync_runs").select("*").order("started_at", { ascending: false }).limit(1),
      supabase.from("payment_decisions").select("*"),
      // Admin-only by policy, so everyone else gets an empty list and the grid
      // falls back to showing nothing rather than failing.
      supabase.from("xero_cash_position").select("*").order("snapshot_date", { ascending: false }).limit(400),
      // Bounded deliberately. PostgREST caps a query at a thousand rows and
      // says nothing when it does, so an unbounded select silently loses the
      // rest — and this table grows by every bill the business ever pays. The
      // forecast never looks back further than its history window, so a year
      // is generous, and the explicit limit means a future overrun fails
      // visibly rather than quietly dropping the newest rows.
      supabase
        .from("xero_paid_bills")
        .select("*")
        .gte("paid_date", isoDate(addDays(new Date(), -400)))
        .order("paid_date", { ascending: false })
        .limit(5000),
    ]);

  const firstError = [
    invoices, bills, scoroOverrides, expected, docSpend, recurring, amexCharges, syncRuns, updates,
  ].find((r) => r.error);
  if (firstError) throw firstError.error;

  // v1 stored Scoro expected dates in its own table; v2 generalised them. Both
  // are read so nothing entered in v1 is lost, with the newer table winning.
  const expectedDates = {};
  for (const o of scoroOverrides.data || []) {
    expectedDates[`${o.doc_type}:${o.doc_id}`] = o.expected_date;
  }
  for (const e of expected.data || []) {
    const kind = e.doc_source === "xero_invoice" ? "invoice" : e.doc_source;
    expectedDates[`${kind}:${e.doc_id}`] = e.expected_date;
  }

  const updatesByDoc = {};
  for (const u of updates.data || []) {
    const key = `${u.doc_source}:${u.doc_id}`;
    (updatesByDoc[key] = updatesByDoc[key] || []).push(u);
  }

  return {
    scoroInvoices: invoices.data || [],
    scoroBills: bills.data || [],
    expectedDates,
    docSpend: docSpend.data || [],
    recurring: recurring.data || [],
    amexSettings: amexSettings.data || null,
    amexCharges: amexCharges.data || [],
    lastSync: (syncRuns.data || [])[0] || null,
    updatesByDoc,
    quotes: quotes.data || [],
    quoteStatuses: Object.fromEntries((quoteStatuses.data || []).map((s) => [s.status, s])),
    quoteRules: Object.fromEntries((quoteRules.data || []).map((r) => [r.status, r])),
    onAccount: onAccount.data || [],
    quoteOverrides: Object.fromEntries((quoteOverrides.data || []).map((o) => [o.quote_id, o])),
    paidInvoices: paidInvoices.data || [],
    instalments: instalments.data || [],
    amexPayments: amexPayments.data || [],
    paidMarks: Object.fromEntries((paidMarks.data || []).map((m) => [`${m.doc_source}:${m.doc_id}`, m])),
    dismissals: Object.fromEntries((dismissals.data || []).map((m) => [`${m.doc_source}:${m.doc_id}`, m])),
    retainers: retainers.data || [],
    retainerSettings: Object.fromEntries((retainerSettings.data || []).map((r) => [r.project_id, r])),
    manualEntries: manualEntries.data || [],
    appSettings: appSettings.data || null,
    investorComments: investorComments.data || [],
    paydownOverrides: Object.fromEntries(
      (paydownOverrides.data || []).map((o) => [o.paydown_date, o])
    ),
    recurringOverrides: Object.fromEntries(
      (recurringOverrides.data || []).map((o) => [`${o.recurring_id}:${o.occurrence_date}`, o])
    ),
    xeroBills: xeroBills.data || [],
    lastXeroSync: (xeroRuns.data || [])[0] || null,
    xeroPaidBills: xeroPaidBills.data || [],
    // What the bank actually said, by the Sunday of the week it fell in. Where
    // a week has several readings the latest one wins — that is the closest
    // thing to a closing balance we have.
    bankByWeekEnd: (() => {
      const out = {};
      for (const c of bankReadings.data || []) {
        const d = new Date(`${c.snapshot_date}T00:00:00Z`);
        const sun = new Date(d);
        sun.setUTCDate(sun.getUTCDate() + ((7 - d.getUTCDay()) % 7));
        const key = sun.toISOString().slice(0, 10);
        const seen = out[key];
        if (!seen || c.snapshot_date > seen.on) {
          out[key] = { balance: Number(c.cash_balance) || 0, on: c.snapshot_date };
        }
      }
      return out;
    })(),
    paymentDecisions: Object.fromEntries(
      (decisions.data || []).map((d) => [`${d.doc_source}:${d.doc_id}`, d])
    ),
  };
}

/**
 * Route an expected date to whichever table owns that kind of document.
 *
 * Three homes, not one: Scoro invoices and bills keep theirs in v1's table so
 * both apps agree; a quote's date lives with its forecast override, because a
 * quote's timing and its inclusion are the same decision; everything else uses
 * the general table. Sending a quote to the general table violates its
 * doc_source check constraint, which is what broke moving quotes in bulk.
 */
export async function saveExpectedDate(sourceType, id, date) {
  if (sourceType === "scoro_quote") {
    if (!date) {
      // Clearing a quote's date returns it to its estimated closing date rather
      // than removing it from the forecast.
      const { error } = await supabase
        .from("quote_forecast_overrides")
        .update({ expected_date: null })
        .eq("quote_id", Number(id));
      if (error) throw error;
      return;
    }
    const { error } = await supabase.from("quote_forecast_overrides").upsert(
      { quote_id: Number(id), expected_date: date, updated_at: new Date().toISOString() },
      { onConflict: "quote_id" }
    );
    if (error) throw error;
    return;
  }

  const scoroKind =
    sourceType === "scoro_invoice" ? "invoice" : sourceType === "scoro_bill" ? "bill" : null;
  if (scoroKind) {
    if (!date) {
      const { error } = await supabase
        .from("scoro_payment_overrides")
        .delete()
        .eq("doc_type", scoroKind)
        .eq("doc_id", id);
      if (error) throw error;
      return;
    }
    const { error } = await supabase.from("scoro_payment_overrides").upsert(
      { doc_type: scoroKind, doc_id: Number(id), expected_date: date, updated_at: new Date().toISOString() },
      { onConflict: "doc_type,doc_id" }
    );
    if (error) throw error;
    return;
  }
  const { error } = await supabase.from("expected_payment_dates").upsert(
    { doc_source: sourceType, doc_id: String(id), expected_date: date, updated_at: new Date().toISOString() },
    { onConflict: "doc_source,doc_id" }
  );
  if (error) throw error;
}

export async function addUpdate(docSource, docId, note, authorEmail) {
  const { data, error } = await supabase
    .from("doc_updates")
    .insert({ doc_source: docSource, doc_id: String(docId), note, author_email: authorEmail })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

export async function saveAmexSettings(settings) {
  const { error } = await supabase
    .from("amex_settings")
    .upsert({ id: true, ...settings, updated_at: new Date().toISOString() }, { onConflict: "id" });
  if (error) throw error;
}

export async function saveRecurring(rows) {
  const { error } = await supabase.from("recurring_costs").insert(rows);
  if (error) throw error;
}

/**
 * Import in chunks: a few years of statement lines exceeds what one PostgREST
 * request will take, and a partial import is worse than a slow one.
 */
export async function saveImport({ filename, kind, headers, columnMap, rows }) {
  const dates = rows.map((r) => r.txn_date).filter(Boolean).sort();
  const { data: imp, error } = await supabase
    .from("imports")
    .insert({
      filename,
      kind,
      headers,
      column_map: columnMap,
      row_count: rows.length,
      date_from: dates[0] || null,
      date_to: dates[dates.length - 1] || null,
    })
    .select("id")
    .single();
  if (error) throw error;

  const CHUNK = 500;
  for (let i = 0; i < rows.length; i += CHUNK) {
    const { error: e } = await supabase
      .from("imported_transactions")
      .insert(rows.slice(i, i + CHUNK).map((r) => ({ ...r, import_id: imp.id })));
    if (e) throw e;
  }
  return imp.id;
}

export async function loadImports() {
  const { data, error } = await supabase
    .from("imports")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function loadImportedTransactions(importId) {
  const all = [];
  const PAGE = 1000;
  for (let from = 0; ; from += PAGE) {
    let q = supabase.from("imported_transactions").select("*").range(from, from + PAGE - 1);
    if (importId) q = q.eq("import_id", importId);
    const { data, error } = await q;
    if (error) throw error;
    all.push(...(data || []));
    if (!data || data.length < PAGE) break;
  }
  return all;
}

export async function saveQuoteRule(status, include, probability, repeatMonthly = false) {
  const { error } = await supabase.from("quote_forecast_rules").upsert(
    { status, include, probability, repeat_monthly: repeatMonthly, updated_at: new Date().toISOString() },
    { onConflict: "status" }
  );
  if (error) throw error;
}

export async function addOnAccountPayment(row) {
  const { error } = await supabase.from("on_account_payments").insert(row);
  if (error) throw error;
}

export async function setOnAccountReconciled(id, reconciled) {
  const { error } = await supabase.from("on_account_payments").update({ reconciled }).eq("id", id);
  if (error) throw error;
}

/**
 * Move several documents' expected dates at once.
 *
 * Sequential rather than parallel: these are two different tables depending on
 * source, and a partial failure should stop with a clear count of what moved
 * rather than leaving an unknown subset changed.
 */
export async function saveExpectedDates(items, date) {
  let moved = 0;
  for (const it of items) {
    await saveExpectedDate(it.type, it.id, date);
    moved++;
  }
  return moved;
}

export async function saveQuoteOverride(quoteId, patch) {
  const { error } = await supabase.from("quote_forecast_overrides").upsert(
    { quote_id: Number(quoteId), ...patch, updated_at: new Date().toISOString() },
    { onConflict: "quote_id" }
  );
  if (error) throw error;
}

/** Removing the override hands the quote back to its stage rule. */
export async function clearQuoteOverride(quoteId) {
  const { error } = await supabase
    .from("quote_forecast_overrides")
    .delete()
    .eq("quote_id", Number(quoteId));
  if (error) throw error;
}

export async function addDocSpend(row) {
  const { error } = await supabase.from("doc_spend").insert(row);
  if (error) throw error;
}

export async function deleteDocSpend(id) {
  const { error } = await supabase.from("doc_spend").delete().eq("id", id);
  if (error) throw error;
}

export async function updateDocSpend(id, patch) {
  const { error } = await supabase.from("doc_spend").update(patch).eq("id", id);
  if (error) throw error;
}

export async function addManualEntry(row) {
  const { error } = await supabase.from("manual_entries").insert(row);
  if (error) throw error;
}

export async function updateManualEntry(id, patch) {
  const { error } = await supabase.from("manual_entries").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteManualEntry(id) {
  const { error } = await supabase.from("manual_entries").delete().eq("id", id);
  if (error) throw error;
}

export async function addInstalments(rows) {
  const { error } = await supabase.from("doc_instalments").insert(rows);
  if (error) throw error;
}

export async function updateInstalment(id, patch) {
  const { error } = await supabase.from("doc_instalments").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteInstalment(id) {
  const { error } = await supabase.from("doc_instalments").delete().eq("id", id);
  if (error) throw error;
}

export async function addAmexPayment(row) {
  const { error } = await supabase.from("amex_payments").insert(row);
  if (error) throw error;
}

export async function updateAmexPayment(id, patch) {
  const { error } = await supabase.from("amex_payments").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteAmexPayment(id) {
  const { error } = await supabase.from("amex_payments").delete().eq("id", id);
  if (error) throw error;
}

export async function markDocPaid(docSource, docId, paidDate, note) {
  const { error } = await supabase.from("doc_paid_marks").upsert(
    { doc_source: docSource, doc_id: String(docId), paid_date: paidDate, note: note || null },
    { onConflict: "doc_source,doc_id" }
  );
  if (error) throw error;
}

export async function unmarkDocPaid(docSource, docId) {
  const { error } = await supabase
    .from("doc_paid_marks")
    .delete()
    .eq("doc_source", docSource)
    .eq("doc_id", String(docId));
  if (error) throw error;
}

export async function dismissDoc(docSource, docId, reason) {
  const { error } = await supabase
    .from("doc_dismissals")
    .upsert({ doc_source: docSource, doc_id: String(docId), reason: reason || null }, { onConflict: "doc_source,doc_id" });
  if (error) throw error;
}

export async function undismissDoc(docSource, docId) {
  const { error } = await supabase
    .from("doc_dismissals")
    .delete()
    .eq("doc_source", docSource)
    .eq("doc_id", String(docId));
  if (error) throw error;
}

export async function saveRetainerSetting(projectId, patch) {
  const { error } = await supabase.from("retainer_settings").upsert(
    { project_id: Number(projectId), ...patch, updated_at: new Date().toISOString() },
    { onConflict: "project_id" }
  );
  if (error) throw error;
}

/**
 * The opening balance, shared rather than personal.
 *
 * It used to live in localStorage, which meant every browser — and the investor
 * link, which has no localStorage of ours at all — could be looking at a
 * different starting cash position while showing the same forecast on top of
 * it. One row, one number.
 */
export async function saveOpeningPosition({ balance, date, overdraft, fxRates }) {
  const patch = { id: 1, updated_at: new Date().toISOString() };
  if (balance !== undefined) patch.opening_balance = balance === "" ? null : Number(balance);
  if (date !== undefined) patch.opening_date = date || null;
  if (overdraft !== undefined) patch.overdraft_limit = overdraft === "" ? null : Number(overdraft);
  if (fxRates !== undefined) patch.fx_rates = fxRates;
  const { error } = await supabase.from("app_settings").upsert(patch, { onConflict: "id" });
  if (error) throw new Error(error.message);
}

/**
 * Running costs: the standing overheads that no invoice ever announces.
 *
 * Everything else in this tool is a mirror of a document, so it is corrected by
 * fixing the document. These rows have no document behind them — they are a
 * statement about how the business runs — so they have to be editable here, and
 * removable, or a wrong assumption becomes permanent furniture.
 */
export async function addRecurringCost(row) {
  const { error } = await supabase.from("recurring_costs").insert(row);
  if (error) throw new Error(error.message);
}

export async function updateRecurringCost(id, patch) {
  const { error } = await supabase.from("recurring_costs").update(patch).eq("id", id);
  if (error) throw new Error(error.message);
}

export async function deleteRecurringCost(id) {
  const { error } = await supabase.from("recurring_costs").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

/** Answer an investor's question. The answer shows on their link. */
export async function answerInvestorComment(id, answer) {
  const { error } = await supabase
    .from("investor_comments")
    .update({ answer, answered_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * Change or refuse a paydown the limit would otherwise force.
 *
 * The forecast can work out what the card needs; it cannot know what the bank
 * can stand. Saying "not this one" leaves the card over its limit, which is the
 * honest consequence rather than a tidier number.
 */
export async function setPaydownOverride(date, patch) {
  const { error } = await supabase
    .from("amex_paydown_overrides")
    .upsert({ paydown_date: date, ...patch, updated_at: new Date().toISOString() }, {
      onConflict: "paydown_date",
    });
  if (error) throw new Error(error.message);
}

export async function clearPaydownOverride(date) {
  const { error } = await supabase
    .from("amex_paydown_overrides")
    .delete()
    .eq("paydown_date", date);
  if (error) throw new Error(error.message);
}

/**
 * Store what the forecast said today, so next week it can be scored.
 *
 * Written once a day, keyed on the week and the day it was taken, so opening
 * the tool five times does not five times overwrite the record of what was
 * believed this morning.
 */
export async function storeForecastSnapshot(weeks) {
  const takenOn = new Date().toISOString().slice(0, 10);
  const rows = weeks
    .filter((w) => !w.isHistory)
    .slice(0, 8)
    .map((w) => ({
      week_start: w.start.toISOString().slice(0, 10),
      taken_on: takenOn,
      forecast_in: Math.round(w.in),
      forecast_out: Math.round(w.out),
      closing: Math.round(w.endBal),
    }));
  if (!rows.length) return;
  await supabase.from("forecast_snapshots").upsert(rows, { onConflict: "week_start,taken_on" });
}

export async function loadSnapshots() {
  const { data } = await supabase
    .from("forecast_snapshots")
    .select("*")
    .order("taken_on", { ascending: true });
  return data || [];
}

/**
 * A retainer that Scoro does not know about.
 *
 * Some clients pay monthly without a Scoro project behind it. Those belong in
 * the forecast just as much, so they live in the same table marked as ours —
 * which is what stops the next sync deleting them.
 */
export async function addManualRetainer({ company_name, name, monthly_net, vat_rate, term_end, day_of_month }) {
  // Negative ids cannot collide with a Scoro project id.
  const { data: existing } = await supabase
    .from("scoro_retainers")
    .select("project_id")
    .lt("project_id", 0)
    .order("project_id", { ascending: true })
    .limit(1);
  const nextId = existing?.length ? existing[0].project_id - 1 : -1;

  const { error } = await supabase.from("scoro_retainers").insert({
    project_id: nextId,
    name: name || `${company_name} retainer`,
    company_name,
    source: "manual",
    derived_monthly_net: Number(monthly_net) || 0,
    derived_monthly: Math.round(Number(monthly_net) * (1 + Number(vat_rate) / 100) * 100) / 100,
    vat_observed: Number(vat_rate) > 0,
    term_end: term_end || null,
    status: "manual",
    synced_at: new Date().toISOString(),
  });
  if (error) throw new Error(error.message);

  await supabase.from("retainer_settings").upsert(
    {
      project_id: nextId,
      include: true,
      vat_rate: Number(vat_rate),
      day_of_month: Number(day_of_month) || 1,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "project_id" }
  );
  return nextId;
}

export async function deleteManualRetainer(projectId) {
  const { error } = await supabase
    .from("scoro_retainers")
    .delete()
    .eq("project_id", projectId)
    .eq("source", "manual");
  if (error) throw new Error(error.message);
}

/** One occurrence of a standing cost that differs from the pattern. */
export async function setRecurringOverride(recurringId, date, patch) {
  const { error } = await supabase.from("recurring_overrides").upsert(
    { recurring_id: recurringId, occurrence_date: date, ...patch, updated_at: new Date().toISOString() },
    { onConflict: "recurring_id,occurrence_date" }
  );
  if (error) throw new Error(error.message);
}

export async function clearRecurringOverride(recurringId, date) {
  const { error } = await supabase
    .from("recurring_overrides")
    .delete()
    .eq("recurring_id", recurringId)
    .eq("occurrence_date", date);
  if (error) throw new Error(error.message);
}

/**
 * Scenarios: a forecast frozen at a moment, plus what you changed about it.
 *
 * The copy is taken here rather than derived on read, so a link sent on Tuesday
 * still shows Tuesday's argument however much the live data has moved since.
 */
export async function createScenario({ name, commentary, fromDate, weeks, txns, openingBalance, openingDate, overdraftLimit, requireCode }) {
  const token = crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().replace(/-/g, "").slice(0, 8);
  const { data, error } = await supabase
    .from("scenario_shares")
    .insert({
      token,
      name,
      commentary: commentary || null,
      from_date: fromDate,
      weeks,
      base: txns,
      opening_balance: openingBalance,
      opening_date: openingDate,
      overdraft_limit: overdraftLimit ?? null,
      require_code: requireCode !== false,
    })
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function loadScenarios() {
  const { data, error } = await supabase
    .from("scenario_shares")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return data || [];
}

export async function updateScenario(id, patch) {
  const { error } = await supabase.from("scenario_shares").update(patch).eq("id", id);
  if (error) throw new Error(error.message);
}

export async function deleteScenario(id) {
  const { error } = await supabase.from("scenario_shares").delete().eq("id", id);
  if (error) throw new Error(error.message);
}

/** Who am I, and what am I allowed to do. */
export async function loadMyProfile() {
  const { data: auth } = await supabase.auth.getUser();
  const id = auth?.user?.id;
  if (!id) return null;
  const { data } = await supabase.from("profiles").select("*").eq("id", id).maybeSingle();
  return data || { id, email: auth.user.email, role: "viewer" };
}

export async function loadProfiles() {
  const { data, error } = await supabase.from("profiles").select("*").order("email");
  if (error) throw new Error(error.message);
  return data || [];
}

export async function setProfileRole(id, role) {
  const { error } = await supabase
    .from("profiles")
    .update({ role, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw new Error(error.message);
}

/**
 * The audit trail. Written by database triggers rather than by this code, so a
 * change made by a script or straight against the database still shows up.
 */
export async function loadAuditLog({ limit = 200, entity = null, actorEmail = null } = {}) {
  let q = supabase.from("audit_log").select("*").order("at", { ascending: false }).limit(limit);
  if (entity) q = q.eq("entity", entity);
  if (actorEmail) q = q.eq("actor_email", actorEmail);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * Invite someone in. The account is created server-side, since it needs the
 * service-role key; Supabase emails them and they choose their own password.
 */
export async function inviteUser(email, role) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Not signed in");
  const res = await fetch("/.netlify/functions/invite-user", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ email, role }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Could not send the invitation");
  return json;
}

/**
 * Note that someone signed in.
 *
 * Recorded from the app because this project keeps no Supabase auth audit, so
 * there is nothing to read. Best effort: a failure here must never stop anyone
 * getting into the tool.
 */
export async function recordSignIn(user) {
  if (!user?.id) return;
  try {
    await supabase.from("sign_ins").insert({
      user_id: user.id,
      email: user.email,
      user_agent: navigator.userAgent?.slice(0, 300) || null,
    });
  } catch {
    // Nothing to do about it, and nothing worth blocking a login over.
  }
}

export async function loadSignIns(limit = 200) {
  const { data, error } = await supabase
    .from("sign_ins")
    .select("*")
    .order("at", { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return data || [];
}

/** Last seen, from Supabase's own user records rather than ours. */
export async function loadLastSeen() {
  const { data, error } = await supabase.rpc("people_last_seen");
  if (error) throw new Error(error.message);
  return data || [];
}

/** Everything one person has done: their sign-ins and their changes. */
export async function loadPersonActivity(email) {
  const [changes, signIns] = await Promise.all([
    loadAuditLog({ limit: 400, actorEmail: email }),
    supabase
      .from("sign_ins")
      .select("*")
      .eq("email", email)
      .order("at", { ascending: false })
      .limit(200)
      .then(({ data }) => data || []),
  ]);
  return { changes, signIns };
}

/**
 * Reverse one change from the audit trail.
 *
 * `force` is only ever sent after the person has been told what else it would
 * undo and has said to go ahead anyway.
 */
export async function undoChange(id, { force = false } = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Not signed in");
  const res = await fetch("/.netlify/functions/undo", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ id, force }),
  });
  const json = await res.json();
  if (!res.ok) {
    const err = new Error(json.error || "Could not undo that");
    err.conflict = !!json.conflict;
    throw err;
  }
  return json;
}

/**
 * Record that a standing cost actually went out on its date.
 *
 * Upserted against the same row that holds a one-off amount change, because
 * both are statements about a single occurrence rather than about the pattern.
 */
export async function setRecurringPaid(id, date, paid) {
  const { error } = await supabase.from("recurring_overrides").upsert(
    {
      recurring_id: id,
      occurrence_date: date,
      paid,
      paid_at: paid ? new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "recurring_id,occurrence_date" }
  );
  if (error) throw error;
}

/** Whether Xero is connected, and how the last sync went. */
export async function loadXeroStatus() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Not signed in");
  const res = await fetch("/.netlify/functions/xero-status", {
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Could not read the Xero connection");
  return json;
}

/**
 * The address that starts a Xero authorisation.
 *
 * The session token rides in the query string because this is a full-page
 * redirect to Xero, not a fetch — there is no way to set a header on it. The
 * function checks the token is an admin's before sending anyone anywhere.
 */
export async function xeroConnectUrl() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Not signed in");
  return `/.netlify/functions/xero-connect?token=${encodeURIComponent(token)}`;
}

/** Ask what merging the hand-entered bills into Xero's would do. */
export async function xeroMerge({ apply = false } = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Not signed in");
  const res = await fetch("/.netlify/functions/xero-merge", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ apply }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Could not work out the merge");
  return json;
}

/** Record why a bill is being paid, or is not. Both halves are required. */
export async function savePaymentDecision(docSource, docId, priority, reason, email) {
  if (!priority) throw new Error("Choose how this is being treated");
  if (!String(reason || "").trim()) throw new Error("A reason is required — that is the point");
  const { error } = await supabase.from("payment_decisions").upsert(
    {
      doc_source: docSource,
      doc_id: String(docId),
      priority,
      reason: String(reason).trim(),
      decided_by: email || null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "doc_source,doc_id" }
  );
  if (error) throw error;
}

export async function clearPaymentDecision(docSource, docId) {
  const { error } = await supabase
    .from("payment_decisions")
    .delete()
    .eq("doc_source", docSource)
    .eq("doc_id", String(docId));
  if (error) throw error;
}

/**
 * Give somebody a new password.
 *
 * Returns it once. There is no way to read an existing password — they are
 * hashed — so this replaces rather than reveals.
 */
export async function resetPassword(id, { mustChange = true } = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Not signed in");
  const res = await fetch("/.netlify/functions/reset-password", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ id, mustChange }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Could not reset the password");
  return json;
}

/** Set the forecast's starting point to the latest bank reading. */
export async function anchorToBank() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) throw new Error("Not signed in");
  const res = await fetch("/.netlify/functions/xero-status", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || "Could not use the bank balance");
  return json;
}
